/**
 * Taco Pin Service
 *
 * Mirrors restroom pin workflow but for taco trucks.
 * Allows users to mark taco truck locations with a 🌮 icon.
 * Pins persist with the same lifecycle controls as restroom pins.
 */

import { telegram } from './telegram';

export interface TacoPin {
  id: string;
  latitude: number;
  longitude: number;
  name: string | null;
  created_at: string;
  active_until: string | null;
  expires_at: string;
  telegram_user_id?: string | null;
  telegram_username?: string | null;
  note?: string | null;
  pin_type: string;
  upvotes: number;
  downvotes: number;
  score: number;
  votes?: number;
  is_ghost: boolean;
  ghost_at: string | null;
  status?: 'active' | 'ghost';
}

class TacoPinService {
  private baseUrl = '/api';

  private normalizePin(raw: any): TacoPin {
    if (!raw) {
      throw new Error('Invalid taco pin payload');
    }

    return {
      id: raw.id,
      latitude: typeof raw.latitude === 'string' ? parseFloat(raw.latitude) : raw.latitude,
      longitude: typeof raw.longitude === 'string' ? parseFloat(raw.longitude) : raw.longitude,
      name: raw.name ?? null,
      created_at: raw.created_at,
      active_until: raw.active_until ?? null,
      expires_at: raw.expires_at,
      telegram_user_id: raw.telegram_user_id ?? raw.user_id ?? null,
      telegram_username: raw.telegram_username ?? raw.user_name ?? null,
      note: raw.note ?? null,
      pin_type: raw.pin_type ?? 'taco_truck',
      upvotes: raw.upvotes ?? 0,
      downvotes: raw.downvotes ?? 0,
      score: raw.score ?? raw.votes ?? 0,
      votes: raw.votes ?? raw.score ?? 0,
      is_ghost: Boolean(raw.is_ghost),
      ghost_at: raw.ghost_at ?? null,
      status: raw.status ?? (raw.is_ghost ? 'ghost' : 'active'),
    };
  }

  private withAuthHeaders(base: Record<string, string> = {}): Record<string, string> {
    const headers: Record<string, string> = { ...base };
    try {
      const initData = telegram.getInitData?.();
      if (initData) {
        headers['X-Telegram-Init-Data'] = initData;
      }
      const userId = telegram.getUserId?.();
      if (userId) {
        headers['X-Telegram-User-Id'] = userId;
      }
      const user = telegram.getUser?.();
      const username = user?.username || user?.first_name;
      if (username) {
        headers['X-Telegram-Username'] = username;
      }
    } catch {
      // Ignore missing Telegram context (e.g., local development)
    }
    return headers;
  }

  async reportPin(
    latitude: number,
    longitude: number,
    userId?: string,
    userName?: string,
    truckName?: string
  ): Promise<TacoPin> {
    const response = await fetch(`${this.baseUrl}/taco-pins`, {
      method: 'POST',
      headers: this.withAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        latitude,
        longitude,
        name: truckName || userName || 'Taco Truck',
        user_id: userId,
        user_name: userName || 'Anonymous',
        telegram_user_id: userId,
        telegram_username: userName || 'Anonymous',
      }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      throw new Error(`Failed to report taco pin: ${response.status} ${errorText}`);
    }

    const data = await response.json();

    const pinPayload = data?.pin ?? data?.data;

    if (!data?.success || !pinPayload) {
      throw new Error('Failed to parse taco pin response');
    }

    return this.normalizePin(pinPayload);
  }

  async getPinsNearby(latitude: number, longitude: number, radiusKm: number = 50): Promise<TacoPin[]> {
    const response = await fetch(
      `${this.baseUrl}/taco-pins?lat=${latitude}&lng=${longitude}&radius=${radiusKm}`,
      { headers: this.withAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch taco pins: ${response.statusText}`);
    }

    const data = await response.json();

    if (Array.isArray(data)) {
      return data.map(pin => this.normalizePin(pin));
    }

    const pinArray = data?.pins ?? data?.data ?? data?.results;

    if (Array.isArray(pinArray)) {
      return pinArray.map((pin: any) => this.normalizePin(pin));
    }

    return [];
  }

  async getPinsInBounds(
    north: number,
    south: number,
    east: number,
    west: number
  ): Promise<TacoPin[]> {
    const response = await fetch(
      `${this.baseUrl}/taco-pins/bounds?north=${north}&south=${south}&east=${east}&west=${west}`,
      { headers: this.withAuthHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch taco pins: ${response.statusText}`);
    }

    const data = await response.json();

    if (Array.isArray(data)) {
      return data.map(pin => this.normalizePin(pin));
    }

    const pinArray = data?.pins ?? data?.data ?? data?.results;

    if (Array.isArray(pinArray)) {
      return pinArray.map((pin: any) => this.normalizePin(pin));
    }

    return [];
  }

  async votePin(pinId: string): Promise<TacoPin> {
    const response = await fetch(`${this.baseUrl}/taco-pins/${pinId}/vote`, {
      method: 'POST',
      headers: this.withAuthHeaders({ 'Content-Type': 'application/json' }),
    });

    if (!response.ok) {
      throw new Error(`Failed to vote on taco pin: ${response.statusText}`);
    }

    const data = await response.json();

    const pinPayload = data?.pin ?? data?.data;

    if (!data?.success || !pinPayload) {
      throw new Error('Failed to parse taco pin vote response');
    }

    return this.normalizePin(pinPayload);
  }

  async deletePin(pinId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/taco-pins/${pinId}`, {
      method: 'DELETE',
      headers: this.withAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete taco pin: ${response.statusText}`);
    }
  }
}

export const tacoPinService = new TacoPinService();
